import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Navigation, Check, Loader2 } from 'lucide-react';

export type MapPickerProps = {
    height?: number;
    initialLat?: number | null;
    initialLng?: number | null;
    defaultCity?: string;
    onPick: (lat: number, lng: number) => void;
};

const CITY_COORDINATES: Record<string, [number, number]> = {
    'شهرقدس': [35.7115, 51.1075],
    'شهریار': [35.6598, 51.0594],
    'باغستان': [35.6297, 51.1042],
    'اندیشه': [35.6983, 50.9995],
    'گرمدره': [35.7482, 51.0772],
    'وردآورد': [35.7423, 51.1444],
    'ملارد': [35.6664, 50.9767],
    'کرج': [35.8327, 50.9915],
    'تهران': [35.6892, 51.3890],
};

const MapPicker: React.FC<MapPickerProps> = ({
    height = 400,
    initialLat,
    initialLng,
    defaultCity = 'شهرقدس',
    onPick
}) => {
    const [lat, setLat] = useState<string>(initialLat ? String(initialLat) : '');
    const [lng, setLng] = useState<string>(initialLng ? String(initialLng) : '');
    const [picked, setPicked] = useState<{ lat: number; lng: number } | null>(
        initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
    );
    const [isLoadingMap, setIsLoadingMap] = useState<boolean>(true);
    const [isLocating, setIsLocating] = useState<boolean>(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const leafletMapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);

    // Initial center coordinates
    const getInitialCenter = (): [number, number] => {
        if (initialLat && initialLng) {
            return [initialLat, initialLng];
        }
        if (defaultCity && CITY_COORDINATES[defaultCity]) {
            return CITY_COORDINATES[defaultCity];
        }
        return [35.7115, 51.1075]; // شهرقدس
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let isMounted = true;

        const initLeaflet = async () => {
            try {
                const L = (await import('leaflet')).default;
                await import('leaflet/dist/leaflet.css');

                if (!isMounted || !mapContainerRef.current) return;

                // Check if map is already initialized on this DOM element
                if (leafletMapRef.current) {
                    leafletMapRef.current.remove();
                    leafletMapRef.current = null;
                }

                const center = getInitialCenter();
                const map = L.map(mapContainerRef.current, {
                    center,
                    zoom: initialLat && initialLng ? 15 : 13,
                    attributionControl: false,
                    zoomControl: true
                });

                // Add attribution in a clean way
                L.control.attribution({ prefix: false, position: 'bottomright' }).addTo(map);

                // Use reliable tile layer with subdomains
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    maxZoom: 19,
                    subdomains: ['a', 'b', 'c']
                }).addTo(map);

                // Create custom SVG marker icon
                const createCustomIcon = () => {
                    return L.divIcon({
                        className: 'salamat-map-marker',
                        html: `
                            <div style="
                                position: relative;
                                width: 38px;
                                height: 38px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                transform: translate(-19px, -38px);
                            ">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="38" height="38" fill="#2563eb" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 3px 6px rgba(0,0,0,0.35));">
                                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0z"/>
                                    <circle cx="12" cy="10" r="3" fill="#ffffff" stroke="none"/>
                                </svg>
                            </div>
                        `,
                        iconSize: [38, 38],
                        iconAnchor: [19, 38]
                    });
                };

                const updateMarker = (selectedLat: number, selectedLng: number) => {
                    if (markerRef.current) {
                        markerRef.current.remove();
                    }
                    markerRef.current = L.marker([selectedLat, selectedLng], {
                        icon: createCustomIcon(),
                        draggable: true
                    }).addTo(map);

                    markerRef.current.on('dragend', (event: any) => {
                        const position = event.target.getLatLng();
                        setLat(position.lat.toFixed(6));
                        setLng(position.lng.toFixed(6));
                        setPicked({ lat: position.lat, lng: position.lng });
                        onPick(position.lat, position.lng);
                    });
                };

                // Place initial marker if coords exist
                if (initialLat && initialLng) {
                    updateMarker(initialLat, initialLng);
                }

                // Handle click on map
                map.on('click', (e: any) => {
                    const { lat: clat, lng: clng } = e.latlng || {};
                    if (typeof clat === 'number' && typeof clng === 'number') {
                        setLat(clat.toFixed(6));
                        setLng(clng.toFixed(6));
                        setPicked({ lat: clat, lng: clng });
                        onPick(clat, clng);
                        updateMarker(clat, clng);
                        setLocationError(null);
                    }
                });

                leafletMapRef.current = map;
                setIsLoadingMap(false);

                // Invalidate size to ensure tiles render properly after DOM calculation
                setTimeout(() => {
                    if (leafletMapRef.current) {
                        leafletMapRef.current.invalidateSize();
                    }
                }, 200);

            } catch (err) {
                console.error('Failed to initialize map:', err);
                setIsLoadingMap(false);
            }
        };

        initLeaflet();

        return () => {
            isMounted = false;
            try {
                if (leafletMapRef.current) {
                    leafletMapRef.current.remove();
                    leafletMapRef.current = null;
                }
            } catch {}
        };
    }, [defaultCity]);

    // Handle center change when default city changes
    useEffect(() => {
        if (leafletMapRef.current && defaultCity && CITY_COORDINATES[defaultCity] && !picked) {
            const coords = CITY_COORDINATES[defaultCity];
            leafletMapRef.current.setView(coords, 13);
        }
    }, [defaultCity, picked]);

    const handleUseCurrentLocation = () => {
        if (!('geolocation' in navigator)) {
            setLocationError('مرورگر شما از قابلیت مکان‌یابی پشتیبانی نمی‌کند.');
            return;
        }

        setIsLocating(true);
        setLocationError(null);

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                setIsLocating(false);
                const currentLat = pos.coords.latitude;
                const currentLng = pos.coords.longitude;
                setLat(currentLat.toFixed(6));
                setLng(currentLng.toFixed(6));
                setPicked({ lat: currentLat, lng: currentLng });
                onPick(currentLat, currentLng);

                if (leafletMapRef.current) {
                    const L = (await import('leaflet')).default;
                    leafletMapRef.current.setView([currentLat, currentLng], 16);

                    if (markerRef.current) {
                        markerRef.current.remove();
                    }

                    const customIcon = L.divIcon({
                        className: 'salamat-map-marker',
                        html: `
                            <div style="
                                position: relative;
                                width: 38px;
                                height: 38px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                transform: translate(-19px, -38px);
                            ">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="38" height="38" fill="#2563eb" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 3px 6px rgba(0,0,0,0.35));">
                                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0z"/>
                                    <circle cx="12" cy="10" r="3" fill="#ffffff" stroke="none"/>
                                </svg>
                            </div>
                        `,
                        iconSize: [38, 38],
                        iconAnchor: [19, 38]
                    });

                    markerRef.current = L.marker([currentLat, currentLng], {
                        icon: customIcon,
                        draggable: true
                    }).addTo(leafletMapRef.current);
                }
            },
            (err) => {
                setIsLocating(false);
                if (err.code === 1) {
                    setLocationError('دسترسی به موقعیت مکانی توسط شما رد شد. می‌توانید از روی نقشه انتخاب کنید.');
                } else {
                    setLocationError('دریافت موقعیت مکانی ناموفق بود. لطفاً از روی نقشه محل را انتخاب کنید.');
                }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const handleSetManually = async () => {
        const nlat = parseFloat(lat);
        const nlng = parseFloat(lng);
        if (Number.isFinite(nlat) && Number.isFinite(nlng) && nlat >= -90 && nlat <= 90 && nlng >= -180 && nlng <= 180) {
            setPicked({ lat: nlat, lng: nlng });
            onPick(nlat, nlng);
            setLocationError(null);

            if (leafletMapRef.current) {
                const L = (await import('leaflet')).default;
                leafletMapRef.current.setView([nlat, nlng], 15);
                if (markerRef.current) {
                    markerRef.current.remove();
                }
                const customIcon = L.divIcon({
                    className: 'salamat-map-marker',
                    html: `
                        <div style="
                            position: relative;
                            width: 38px;
                            height: 38px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            transform: translate(-19px, -38px);
                        ">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="38" height="38" fill="#2563eb" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 3px 6px rgba(0,0,0,0.35));">
                                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0z"/>
                                <circle cx="12" cy="10" r="3" fill="#ffffff" stroke="none"/>
                            </svg>
                        </div>
                    `,
                    iconSize: [38, 38],
                    iconAnchor: [19, 38]
                });
                markerRef.current = L.marker([nlat, nlng], { icon: customIcon, draggable: true }).addTo(leafletMapRef.current);
            }
        } else {
            setLocationError('مختصات وارد شده نامعتبر است.');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span>روی نقشه کلیک کنید تا نشانگر در محل آدرس شما قرار گیرد</span>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleUseCurrentLocation}
                    disabled={isLocating}
                    className="flex items-center gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                    {isLocating ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            در حال دریافت موقعیت...
                        </>
                    ) : (
                        <>
                            <Navigation className="w-3.5 h-3.5" />
                            موقعیت فعلی من
                        </>
                    )}
                </Button>
            </div>

            {locationError && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    {locationError}
                </div>
            )}

            <div className="relative rounded-2xl overflow-hidden border border-blue-200 bg-slate-100 shadow-inner" style={{ height }}>
                {isLoadingMap && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 z-20">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                            در حال بارگذاری نقشه...
                        </div>
                    </div>
                )}
                <div ref={mapContainerRef} className="w-full h-full" style={{ minHeight: height }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end pt-1">
                <div className="space-y-1.5">
                    <Label htmlFor="lat" className="text-xs text-gray-600">عرض جغرافیایی (Lat)</Label>
                    <Input
                        id="lat"
                        value={lat}
                        onChange={(e) => setLat(e.target.value)}
                        placeholder="مثال: 35.7115"
                        className="text-xs h-9 bg-white"
                        dir="ltr"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="lng" className="text-xs text-gray-600">طول جغرافیایی (Lng)</Label>
                    <Input
                        id="lng"
                        value={lng}
                        onChange={(e) => setLng(e.target.value)}
                        placeholder="مثال: 51.1075"
                        className="text-xs h-9 bg-white"
                        dir="ltr"
                    />
                </div>
                <div>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleSetManually}
                        className="w-full h-9 text-xs"
                    >
                        ثبت مختصات دستی
                    </Button>
                </div>
            </div>

            {picked && (
                <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-medium">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span>موقعیت روی نقشه انتخاب شد: ({picked.lat.toFixed(5)}, {picked.lng.toFixed(5)})</span>
                </div>
            )}
        </div>
    );
};

export default MapPicker;
